import { useBoolean } from "../../../../helpers/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateTableSchemaDesc } from "@/helpers/apis";
import { useEffect, useState } from "react";
import { ColorTag, ModifiedByTag } from "@/components/tags";
import { ColumnDatatype } from "@/components/column";
import { EditIcon } from "@/assets/icons";
import { GET_TABLE_BY_ID } from "../../DatasetsDetails";
import styles from "../../styles.module.scss";
import classnames from "classnames";
import { Button, Input } from "@altimateai/lego";
import { WriteIcon } from "@assets/icons";
import { UserListResponse } from "@/types";
import { Column } from "../../types";
import { TagsList } from "@/pages/settings/tags/components/TagsList";
import { getEntityFromScope } from "../../utils";

interface Props {
  column: Column;
  users?: UserListResponse["users"];
  colDescription: string;
  scope: string[];
  datastoreType: string;
}

const ColumnCard = ({ column, users, colDescription, scope, datastoreType }: Props) => {
  const { value: isEditing, setTrue, setFalse } = useBoolean();
  const [description, setDescription] = useState("");
  const { database, schema, table, datastore } = getEntityFromScope(scope);
  useEffect(() => {
    setDescription(colDescription);
  }, [colDescription]);
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: (data: { description: string }) =>
      updateTableSchemaDesc(column.table_schema_id, data),
    onSuccess: data => {
      if (data?.ok) {
        setFalse();
        queryClient.invalidateQueries({ queryKey: [GET_TABLE_BY_ID] });
      }
    },
  });

  return (
    <div className={styles.column_card} aria-label={`${column.name} details`}>
      <div className="al-flex al-items-center">
        <ColumnDatatype datatype={column.datatype} />
        <div className="ms-2">{column.name}</div>
        <div className="spacer" />
        <ColorTag label={column.datatype} color={styles.column_tag} />
      </div>
      <div className="mb-1 divider" />
      <div className="al-flex al-items-center al-justify-between">
        <div className="al-flex gap-xs al-items-center">
          <div>Description</div>
          {description && (
            <EditIcon
              className={classnames("cursor-pointer", styles.edit_icon, {
                [styles.active]: isEditing,
              })}
              onClick={e => {
                e.stopPropagation();
                if (isEditing) {
                  setFalse();
                  setDescription(colDescription);
                } else {
                  setTrue();
                }
              }}
            />
          )}
        </div>
        <div className="al-flex gap-xs al-items-center">
          {description ? (
            <>
              <ModifiedByTag users={users} lastModifiedBy={column.last_modified_by} />
            </>
          ) : (
            <>
              <Button
                onClick={e => {
                  e.stopPropagation();
                  if (isEditing) {
                    setFalse();
                    setDescription(colDescription);
                  } else {
                    setTrue();
                  }
                }}
                size="sm"
                variant="ghost"
                className="!al-bg-action-soft-brand al-text-brand al-font-normal"
              >
                <WriteIcon />
                Write
              </Button>
            </>
          )}
        </div>
      </div>
      {isEditing ? (
        <>
          <Input
            type="textarea"
            className={classnames("mb-1", styles.multiline_input)}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Enter the column description"
          />
          <div className="al-flex gap-xs al-items-center">
            <Button
              onClick={e => {
                e.stopPropagation();
                mutate({ description });
              }}
            >
              Save
            </Button>
            <Button
              variant="outline"
              className={styles.cancel_button}
              onClick={() => {
                setDescription(colDescription);
                setFalse();
              }}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : description ? (
        <div className="text-muted font-normal">{description}</div>
      ) : null}
      <TagsList
        tags={column.tags}
        entity={{
          datastore_type: datastoreType,
          table_rk: table,
          schema_rk: schema,
          datastore_rk: datastore,
          cluster_rk: database,
          column_rk: column.name,
        }}
      />
    </div>
  );
};

export default ColumnCard;
